import Vue from 'vue';
import { saveUserPreferences, getUserPreferences } from '@/api/preferences';
import { dlog } from '@/lib/debugLog';
class ThemeStore {
    // Sparkco restyle (08/10/2026): DARK is the default theme — matches the
    // Sparkco room sidebar the app is embedded in. A saved/server choice wins.
    state = Vue.observable({
        theme: 'dark',
        isLoading: false
    });
    get isDark() {
        return this.state.theme === 'dark';
    }
    get theme() {
        return this.state.theme;
    }
    get isLoading() {
        return this.state.isLoading;
    }
    async initialize() {
        dlog('[ThemeStore] Initializing theme store');
        try {
            this.state.isLoading = true;
            // First check localStorage for immediate UI update
            const savedTheme = localStorage.getItem('theme');
            dlog('[ThemeStore] Saved theme from localStorage:', savedTheme);
            if (savedTheme) {
                this.setTheme(savedTheme);
            }
            // Then fetch from server
            const preferences = await getUserPreferences();
            dlog('[ThemeStore] Preferences from server:', preferences);
            // Only use server preference if we don't have a local one
            // This prevents the server from overriding user's choice
            if (preferences?.theme && !savedTheme) {
                this.setTheme(preferences.theme);
            }
            else if (savedTheme && preferences?.theme !== savedTheme) {
                // If local and server differ, save local preference to server
                this.savePreferences();
            }
            // No local and no server choice → paint the default (dark) without
            // persisting it, so the system-theme listener stays in charge.
            if (!savedTheme && !preferences?.theme) {
                this.applyToDom(this.state.theme);
            }
        }
        catch (error) {
            console.error('[ThemeStore] Failed to load preferences:', error);
            // Use system preference as fallback
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            this.setTheme(prefersDark ? 'dark' : 'light');
        }
        finally {
            this.state.isLoading = false;
        }
    }
    setTheme(theme) {
        dlog('[ThemeStore] Setting theme to:', theme);
        this.state.theme = theme;
        // Update DOM
        const root = this.applyToDom(theme);
        // Save to localStorage for immediate persistence
        localStorage.setItem('theme', theme);
        dlog('[ThemeStore] Theme set, localStorage updated, dark class present:', root.classList.contains('dark'));
    }
    /**
     * The ONE theme switch: `dark` class (legacy selectors + MutationObservers)
     * mirrored to `data-theme` (styles/theme.css — dark on :root by default,
     * light only under [data-theme="light"]).
     */
    applyToDom(theme) {
        const root = document.documentElement;
        if (theme === 'dark') {
            root.classList.add('dark');
        }
        else {
            root.classList.remove('dark');
        }
        root.setAttribute('data-theme', theme);
        return root;
    }
    async toggleTheme() {
        const newTheme = this.state.theme === 'light' ? 'dark' : 'light';
        this.setTheme(newTheme);
        // Save to server (don't await to keep UI responsive)
        this.savePreferences();
    }
    async savePreferences() {
        try {
            const saved = await saveUserPreferences({ theme: this.state.theme });
            if (saved === true) {
                dlog('[ThemeStore] Preferences saved successfully');
            }
            else if (saved === false) {
                console.warn('[ThemeStore] Preferences not saved to server (request failed)');
            }
        }
        catch (error) {
            console.error('[ThemeStore] Failed to save preferences:', error);
            // Continue working even if save fails
        }
    }
    // Listen for system theme changes
    setupSystemThemeListener() {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', (e) => {
            // Only auto-switch if user hasn't manually set a preference
            if (!localStorage.getItem('theme')) {
                this.setTheme(e.matches ? 'dark' : 'light');
            }
        });
    }
}
export const themeStore = new ThemeStore();
